import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowUpRight, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUPPORT_WHATSAPP_NUMBER = "919036219589";
const WHATSAPP_MESSAGE = "Hi! I'm interested in the Salesforce Data Cloud Certification Program (₹20,000, 8 weeks, live + self-paced). Please share more details on how to enroll.";
export const CERTIFICATION_WHATSAPP_LINK = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

/** Lightweight enquiry modal for the Salesforce Data Cloud Certification
 * Program page — a simplified, single-track version of TrainingInquiryModal
 * (no track selector needed since this page is about ONE specific paid
 * program). Reuses the existing /api/leads/submit endpoint. */
export default function CertificationEnquiryModal({ open, onClose }) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/submit", {
        name: name.trim(), phone: phone.trim(), email: email.trim() || undefined,
        track: "data_cloud_certification", message: message.trim() || undefined,
        source: "certification_program_page",
      });
      setSubmitted(true);
      toast.success("Request received! Our team will call you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your request. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="certification-enquiry-modal" onClick={handleClose}>
      <div className="glass-panel rounded-2xl p-6 sm:p-8 max-w-2xl w-full border border-amber-400/20 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={handleClose} data-testid="certification-enquiry-modal-close-button" className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-white mb-1">Enquire About the Certification Program</h2>
        <p className="text-sm text-slate-400 mb-5">Salesforce Data Cloud Certification Program — ₹20,000, 8 weeks, live + self-paced.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-5 flex flex-col">
            <p className="text-sm font-semibold text-white mb-1">Chat on WhatsApp</p>
            <p className="text-xs text-slate-400 mb-4 flex-1">Get instant answers about batch dates, curriculum, and enrollment from our advisor.</p>
            <a href={CERTIFICATION_WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="certification-enquiry-whatsapp-button" className="flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors">
              Chat Now <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-5">
            {submitted ? (
              <div className="flex flex-col items-center text-center py-4" data-testid="certification-enquiry-form-success">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                <p className="text-sm font-semibold text-white">Request received!</p>
                <p className="text-xs text-slate-400 mt-1">Our team will call you shortly to help you enroll.</p>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-2.5" data-testid="certification-enquiry-form">
                <p className="text-sm font-semibold text-white mb-1">Request a Callback</p>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Name" data-testid="certification-enquiry-name-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone Number" data-testid="certification-enquiry-phone-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" data-testid="certification-enquiry-email-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Your query (optional)" rows={2} data-testid="certification-enquiry-message-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                <button type="submit" disabled={submitting} data-testid="certification-enquiry-submit-button" className="w-full flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors">
                  {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Request Callback
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
